import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiRefreshCw, FiUsers, FiPieChart, FiZap, FiDollarSign, FiFilter, FiBarChart2, FiSearch, FiArrowRight } from 'react-icons/fi';
import Layout from '../components/Layout';
import theme from '../styles/theme';
import { contratosAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${theme.spacing[4]};
`;

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.text.muted};
  margin: 0;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const GhostButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border-radius: ${theme.borderRadius.xl};
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[600]};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${theme.spacing[4]};
`;

const StatCard = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const StatLabel = styled.span`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  text-transform: uppercase;
`;

const StatValue = styled.span`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['3xl']};
  font-weight: 700;
`;

const StatBadge = styled.span`
  align-self: flex-start;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
`;

const Card = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const TableWrapper = styled.div`
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 520px;
`;

const Th = styled.th`
  text-align: left;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const Td = styled.td`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border.primary};
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.text.secondary};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[8]} ${theme.spacing[4]};
  text-align: center;
  color: ${theme.colors.text.muted};
`;

const ClientSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const ClientForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[3]};
  align-items: center;
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  min-width: 220px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const PrimaryButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid
    ${({ $type }) =>
      $type === 'error'
        ? theme.colors.error[500]
        : $type === 'success'
        ? theme.colors.success[500]
        : theme.colors.warning[500]};
  background: ${({ $type }) =>
    $type === 'error'
      ? theme.colors.error[100]
      : $type === 'success'
      ? theme.colors.success[100]
      : theme.colors.warning[100]};
  color: ${({ $type }) =>
    $type === 'error'
      ? theme.colors.error[700]
      : $type === 'success'
      ? theme.colors.success[700]
      : theme.colors.warning[700]};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
`;

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value || 0);

const ContratosDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [dadosGerais, setDadosGerais] = useState(null);
  const [clienteId, setClienteId] = useState('');
  const [loadingCliente, setLoadingCliente] = useState(false);
  const [erroCliente, setErroCliente] = useState(null);
  const [dadosCliente, setDadosCliente] = useState(null);

  const carregarDashboard = async () => {
    try {
      setLoading(true);
      setErro(null);
      const response = await contratosAPI.dashboardGeral();
      if (response.data?.success) {
        setDadosGerais(response.data.data);
      } else {
        setErro('Não foi possível carregar o dashboard geral.');
      }
    } catch (error) {
      console.error('Erro ao carregar dashboard geral', error);
      setErro('Erro ao carregar dashboard geral.');
    } finally {
      setLoading(false);
    }
  };

  const carregarDashboardCliente = async () => {
    const id = Number.parseInt(clienteId, 10);
    if (!Number.isFinite(id)) {
      setErroCliente('Informe um ID de cliente válido.');
      return;
    }

    try {
      setErroCliente(null);
      setLoadingCliente(true);
      const response = await contratosAPI.dashboardCliente(id);
      if (response.data?.success) {
        setDadosCliente(response.data.data);
      } else {
        setErroCliente('Não foi possível carregar os dados do cliente.');
      }
    } catch (error) {
      console.error('Erro ao carregar dashboard do cliente', error);
      setErroCliente('Erro ao carregar dashboard do cliente.');
    } finally {
      setLoadingCliente(false);
    }
  };

  useEffect(() => {
    carregarDashboard();
  }, []);

  return (
    <Layout title="Dashboard de Contratos e Créditos">
      <Container>
        <Header>
          <HeaderGroup>
            <Title>Visão geral de créditos</Title>
            <Subtitle>
              Acompanhe o consumo, extensões e saldos dos contratos ativos.
            </Subtitle>
          </HeaderGroup>
          <ActionBar>
            <GhostButton onClick={carregarDashboard}>
              <FiRefreshCw />
              Atualizar
            </GhostButton>
          </ActionBar>
        </Header>

        {erro && (
          <Alert $type="error">
            {erro}
          </Alert>
        )}

        {loading && !erro && (
          <Alert $type="warning">
            Carregando métricas...
          </Alert>
        )}

        {!loading && dadosGerais && (
          <>
            <Grid>
              <StatCard>
                <StatLabel>Total de créditos usados no mês</StatLabel>
                <StatValue>{dadosGerais.totalCreditosUsadosMes}</StatValue>
                <StatBadge>
                  <FiZap />
                  Consumo acumulado
                </StatBadge>
              </StatCard>
              <StatCard>
                <StatLabel>Créditos pendentes</StatLabel>
                <StatValue>{dadosGerais.creditosPendentes}</StatValue>
                <StatBadge>
                  <FiPieChart />
                  Saldo em aberto
                </StatBadge>
              </StatCard>
              <StatCard>
                <StatLabel>Contratos ativos</StatLabel>
                <StatValue>{dadosGerais.contratosAtivos}</StatValue>
                <StatBadge>
                  <FiUsers />
                  Clientes atendidos
                </StatBadge>
              </StatCard>
              <StatCard>
                <StatLabel>Extensões vendidas (mês)</StatLabel>
                <StatValue>{formatCurrency(dadosGerais.extensoesVendidas.totalValor)}</StatValue>
                <StatBadge>
                  <FiDollarSign />
                  {dadosGerais.extensoesVendidas.totalCreditos} créditos extras
                </StatBadge>
              </StatCard>
            </Grid>

            <Card>
              <CardHeader>
                <CardTitle>
                  <FiBarChart2 />
                  Contratos por status
                </CardTitle>
              </CardHeader>
              <Grid>
                {Object.entries(dadosGerais.contratosPorStatus || {}).map(([status, quantidade]) => (
                  <StatCard key={status}>
                    <StatLabel>Status</StatLabel>
                    <StatValue>{quantidade}</StatValue>
                    <StatBadge>{status}</StatBadge>
                  </StatCard>
                ))}
                {Object.entries(dadosGerais.contratosPorStatus || {}).length === 0 && (
                  <EmptyState>Sem dados de status disponíveis.</EmptyState>
                )}
              </Grid>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <FiPieChart />
                  Consumo por plano
                </CardTitle>
              </CardHeader>
              {dadosGerais.consumoPorPlano && dadosGerais.consumoPorPlano.length > 0 ? (
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Plano</Th>
                        <Th>Créditos consumidos</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {dadosGerais.consumoPorPlano.map((item) => (
                        <tr key={item.plano}>
                          <Td>{item.plano}</Td>
                          <Td>{item.total_consumido}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrapper>
              ) : (
                <EmptyState>Nenhum consumo registrado neste mês.</EmptyState>
              )}
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <FiUsers />
                  Top clientes por consumo
                </CardTitle>
              </CardHeader>
              {dadosGerais.topClientes && dadosGerais.topClientes.length > 0 ? (
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Cliente</Th>
                        <Th>Créditos consumidos</Th>
                        <Th>Contratos</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {dadosGerais.topClientes.map((cliente) => (
                        <tr key={cliente.cliente_id}>
                          <Td>
                            Cliente {cliente.cliente_id}
                            <div>
                              <GhostButton
                                style={{ marginTop: theme.spacing[2] }}
                                onClick={() => {
                                  setClienteId(String(cliente.cliente_id));
                                  carregarDashboardCliente();
                                }}
                              >
                                <FiSearch />
                                Ver detalhes
                              </GhostButton>
                            </div>
                          </Td>
                          <Td>{cliente.total_consumido}</Td>
                          <Td>
                            {(cliente.contratos || []).map((contrato) => (
                              <Badge key={contrato.contrato_id}>
                                #{contrato.contrato_id} • {contrato.consumo} créditos
                              </Badge>
                            ))}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrapper>
              ) : (
                <EmptyState>Nenhum cliente com consumo registrado.</EmptyState>
              )}
            </Card>
          </>
        )}

        <Card>
          <CardHeader>
            <CardTitle>
              <FiFilter />
              Visão individual do cliente
            </CardTitle>
          </CardHeader>
          <ClientSection>
            <ClientForm>
              <FormRow>
                <Input
                  type="number"
                  min="1"
                  placeholder="ID do cliente"
                  value={clienteId}
                  onChange={(e) => setClienteId(e.target.value)}
                />
                <PrimaryButton onClick={carregarDashboardCliente} disabled={loadingCliente}>
                  <FiSearch />
                  {loadingCliente ? 'Buscando...' : 'Pesquisar'}
                </PrimaryButton>
              </FormRow>
              {erroCliente && (
                <Alert $type="error">{erroCliente}</Alert>
              )}
              {dadosCliente && (
                <Alert $type="success">
                  Cliente {dadosCliente.cliente_id} localizado. {dadosCliente.contratos.length} contratos encontrados.
                </Alert>
              )}
            </ClientForm>

            {dadosCliente ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
                <Grid>
                  <StatCard>
                    <StatLabel>Créditos usados</StatLabel>
                    <StatValue>{dadosCliente.totais.creditos_usados}</StatValue>
                  </StatCard>
                  <StatCard>
                    <StatLabel>Saldo atual</StatLabel>
                    <StatValue>{dadosCliente.totais.creditos_saldo}</StatValue>
                  </StatCard>
                  <StatCard>
                    <StatLabel>Extensões (créditos)</StatLabel>
                    <StatValue>{dadosCliente.totais.extensoes_creditos}</StatValue>
                  </StatCard>
                  <StatCard>
                    <StatLabel>Extensões (valor)</StatLabel>
                    <StatValue>{formatCurrency(dadosCliente.totais.extensoes_valor)}</StatValue>
                  </StatCard>
                </Grid>

                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Contrato</Th>
                        <Th>Plano</Th>
                        <Th>Status</Th>
                        <Th>Saldo atual</Th>
                        <Th>Ações</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {dadosCliente.contratos.map((contrato) => (
                        <tr key={contrato.id}>
                          <Td>#{contrato.id}</Td>
                          <Td>{contrato.nome_plano}</Td>
                          <Td>{contrato.status}</Td>
                          <Td>{contrato.saldo_atual}</Td>
                          <Td>
                            <GhostButton onClick={() => navigate(`/gestao/contratos/${contrato.id}`)}>
                              <FiArrowRight />
                              Abrir contrato
                            </GhostButton>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrapper>
              </div>
            ) : (
              <EmptyState>
                Informe o ID do cliente para visualizar o detalhamento individual.
              </EmptyState>
            )}
          </ClientSection>
        </Card>
      </Container>
    </Layout>
  );
};

export default ContratosDashboard;


